import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'
import { useRouter } from 'expo-router'
import { useForm } from 'react-hook-form'
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/base'
import { TxInput } from '@/components/form-control'
import NavigationBar from '@/components/NavigationBar'

type Password = {
  withdrawalPassword: string
}

const defaultPassword: Password = {
  withdrawalPassword: ''
}

interface SecurityVerificationState {
  cb: (withdrawalPassword: string) => void | null
  setCb: (fn: (withdrawalPassword: string) => void) => void
  open: () => void
  close: () => void
  offset: any
  useFormReturnValue: any
}

export const SecurityVerificationContext = createContext<SecurityVerificationState>({
  cb: () => {},
  setCb: () => {},
  open: () => {},
  close: () => {},
  offset: 0,
  useFormReturnValue: {}
})

interface SecurityVerificationProviderProps {
  children: ReactNode
}

export function SecurityVerificationProvider({ children }: SecurityVerificationProviderProps) {
  const { height: deviceHeight } = useWindowDimensions()
  const [cb, setCb] = useState<(withdrawalPassword: string) => void>(() => () => {})
  const [isOpen, setIsOpen] = useState(false)
  const offset = useSharedValue(deviceHeight)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => {
    offset.value = withTiming(deviceHeight, { duration: 300 }, () => {
      runOnJS(setIsOpen)(false)
    })
  }, [])
  const useFormReturnValue = useForm<Password>({ defaultValues: defaultPassword })
  const value = useMemo(() => ({ cb, setCb, isOpen, open, close, offset, useFormReturnValue }), [cb])

  return (
    <SecurityVerificationContext.Provider value={value}>
      {children}
      {isOpen && <SecurityVerificationScreen />}
    </SecurityVerificationContext.Provider>
  )
}

function SecurityVerificationScreen() {
  const { t } = useTranslation()
  const router = useRouter()

  const {
    close,
    cb,
    offset,
    useFormReturnValue: { watch, control, handleSubmit, reset }
  } = useContext(SecurityVerificationContext)
  const withdrawalPassword = watch('withdrawalPassword')

  const viewStyle = useAnimatedStyle(() => {
    return { transform: [{ translateY: offset.value }] }
  })

  const onSubmit = (formData: Password) => {
    reset()
    cb(formData.withdrawalPassword)
  }

  const handleForgotPassword = () => {
    close()
    reset()
    router.navigate('/setting/tx-password')
  }

  useEffect(() => {
    offset.value = withTiming(0, { duration: 500 })
  }, [])

  return (
    <Animated.View style={[styles.overlay, viewStyle]}>
      <NavigationBar
        iconId={'x'}
        handleAction={() => {
          close()
          reset()
        }}
      >
        {t('ui:securityVerification')}
      </NavigationBar>
      <View className={'flex-1 bg-background-secondary p-4'}>
        <View className={'gap-4 rounded-lg border border-border-10 bg-background-primary p-4'}>
          <TxInput control={control} name={'withdrawalPassword'} onForgotPassword={handleForgotPassword} />
          <Button size={'md'} onPress={handleSubmit(onSubmit)} disabled={withdrawalPassword.length !== 6}>
            {t('ui:confirm')}
          </Button>
        </View>
      </View>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 10
  }
})

interface SecurityVerificationTriggerProps {
  onPress?: () => void
  className?: string
  children: string
  disabled?: boolean
}

export function SecurityVerificationTrigger({
  onPress,
  className,
  children,
  disabled
}: SecurityVerificationTriggerProps) {
  return (
    <Button
      size={'md'}
      intent={'primary'}
      variant={'solid'}
      onPress={onPress}
      className={className}
      disabled={disabled}
    >
      {children}
    </Button>
  )
}
